import React from 'react';
import PropTypes from 'prop-types';
import JSON5 from 'json5';
import Icon from 'components/icon';
import Button from 'components/button';
import StorefrontCodeFileList from './file-list';
import StorefrontCodeMirror from './code-mirror';
import Loading from 'components/loading';
import { classNames, locationWithQuery } from 'utils';
import { getLokiDb } from 'services/lokidb';

import './code.scss';

export default class StorefrontCodeEditor extends React.PureComponent {
  static contextTypes = {
    openModal: PropTypes.func.isRequired,
    notifySuccess: PropTypes.func.isRequired,
    notifyError: PropTypes.func.isRequired,
  };

  static propTypes = {
    router: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    storefront: PropTypes.object.isRequired,
    themeConfigs: PropTypes.object.isRequired,
    updateThemeConfig: PropTypes.func.isRequired,
    onEdited: PropTypes.func.isRequired,
  };

  state = {
    saving: false,
    openFiles: [],
    activeFile: null,
    edited: false,
    editedFiles: [],
    cachedFiles: null,
  };

  fileCache = null;

  async componentDidMount() {
    await getLokiDb('themeCodeFileCache', (collection) => {
      this.fileCache = collection;
      this.openInitialFiles();
    });

    document.addEventListener('keydown', this.onKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown);
  }

  onKeyDown = (event) => {
    if (event.metaKey || event.ctrlKey) {
      switch (event.code) {
        case 'KeyS':
          event.preventDefault();
          event.stopPropagation();
          this.onSaveActiveFile();
          break;

        default:
          break;
      }
    }
  };

  openInitialFiles() {
    const { storefront, themeConfigs, location } = this.props;

    let cachedFiles = this.fileCache.findOne({
      storefront_id: storefront.id,
      theme_id: storefront.theme_id,
    });

    if (!cachedFiles) {
      cachedFiles = this.fileCache.insert({
        storefront_id: storefront.id,
        theme_id: storefront.theme_id,
        openFiles: [],
        activeFile: null,
      });
    }

    const openFiles = [];
    const activeFilePath = location.query.file || cachedFiles?.activeFile;
    const activeFile = this.formatFileData(
      themeConfigs.results.find((file) => file.file_path === activeFilePath),
    );

    if (cachedFiles?.openFiles) {
      openFiles.push(
        ...cachedFiles.openFiles
          .map((filePath) =>
            themeConfigs.results.find((f) => f.file_path === filePath),
          )
          .filter(Boolean)
          .map((file) => this.formatFileData(file)),
      );
    }

    if (activeFile) {
      this.setSelectedFile(activeFile);

      if (openFiles.every((file) => file.id !== activeFile.id)) {
        openFiles.push(activeFile);
      }
    }

    this.setState(
      {
        cachedFiles,
        openFiles,
        activeFile: activeFile || null,
      },
      () => this.updateCachedFiles(),
    );
  }

  updateCachedFiles() {
    const { cachedFiles, openFiles, activeFile } = this.state;

    if (!cachedFiles) {
      return;
    }

    cachedFiles.activeFile = activeFile?.file_path || null;
    cachedFiles.openFiles = openFiles.map((file) => file.file_path);

    this.fileCache.update(cachedFiles);
  }

  setSelectedFile(file) {
    const { router } = this.props;

    router.replace(locationWithQuery(location, { file: file.file_path }));
  }

  onClickOpenFile = (file) => {
    const { openFiles, saving } = this.state;

    if (saving) {
      return;
    }

    const activeFile = this.formatFileData(file);

    const nextOpenFiles = openFiles.some((f) => f.id === file.id)
      ? openFiles
      : [...openFiles, activeFile];

    this.setState(
      {
        openFiles: nextOpenFiles,
        activeFile,
      },
      () => this.updateCachedFiles(),
    );

    this.setSelectedFile(file);
  };

  formatFileData(file) {
    if (!file || !file.file_path.endsWith('.json')) {
      return file;
    }

    try {
      file.file_data = JSON.stringify(JSON5.parse(file.file_data), null, 2);
      return file;
    } catch (e) {
      // noop
    }

    return file;
  }

  validateFileData(file) {
    if (file.file_path.endsWith('.json')) {
      try {
        JSON5.parse(file.file_data);
      } catch (err) {
        return [err.message.replace('JSON5: ', '')];
      }
    }

    return null;
  }

  setEditedFileErrors(editedFile, errors) {
    const { editedFiles } = this.state;

    if (editedFile) {
      const nextEditedFiles = editedFiles.map((file) =>
        file.id === editedFile.id
          ? { ...file, file_data_errors: errors }
          : file,
      );

      this.setState({ editedFiles: nextEditedFiles });
    }
  }

  onClickCloseFile = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const { id } = event.currentTarget.dataset;
    const { openFiles, editedFiles, saving } = this.state;
    const { openModal } = this.context;

    if (saving) {
      return;
    }

    const file = openFiles.find((file) => file.id === id);
    const editedFile = this.getEditedFile(file);

    if (editedFile) {
      openModal('ConfirmRouteLeave', {
        onConfirm: () => {
          this.closeFileTab(file);
          this.setState({
            editedFiles: editedFiles.filter((f) => f.id !== file.id),
          });
        },
      });
    } else {
      this.closeFileTab(file);
    }
  };

  closeFileTab(file) {
    const { openFiles, activeFile } = this.state;

    const nextOpenFiles = openFiles.filter((f) => f.id !== file.id);
    const nextActiveFile =
      activeFile?.id === file.id ? nextOpenFiles[0] || null : activeFile;

    this.setState(
      {
        openFiles: nextOpenFiles,
        activeFile: nextActiveFile,
      },
      () => this.updateCachedFiles(),
    );
  }

  onClickFileTab = (event) => {
    const { id } = event.currentTarget.dataset;
    const { openFiles, saving } = this.state;

    if (saving) {
      return;
    }

    const activeFile = openFiles.find((file) => file.id === id);

    this.setState(
      {
        activeFile,
      },
      () => this.updateCachedFiles(),
    );

    this.setSelectedFile(activeFile);
  };

  getEditedFile(file) {
    const { editedFiles } = this.state;

    return editedFiles.find((f) => f.id === file.id);
  }

  onChangeActiveFile = (value) => {
    const { onEdited } = this.props;
    const { activeFile, editedFiles, saving } = this.state;

    if (saving) {
      return;
    }

    const editedFile = this.getEditedFile(activeFile);

    const isEdited = value !== activeFile.file_data;

    const nextEditedFiles = isEdited
      ? editedFile
        ? editedFiles.map((file) =>
            file.id === activeFile.id ? { ...file, file_data: value } : file,
          )
        : [...editedFiles, { ...activeFile, file_data: value }]
      : editedFiles.filter((file) => file.id !== activeFile.id);

    const edited = Boolean(nextEditedFiles.length);

    if (onEdited) {
      onEdited(edited);
    }

    this.setState({
      edited,
      editedFiles: nextEditedFiles,
    });
  };

  onSaveActiveFile = async () => {
    const { updateThemeConfig, onEdited } = this.props;
    const { activeFile, editedFiles, saving } = this.state;
    const { notifySuccess, notifyError } = this.context;

    let editedFile = this.getEditedFile(activeFile);
    if (!editedFile || saving) {
      return;
    }

    const errors = this.validateFileData(editedFile);
    if (errors) {
      this.setEditedFileErrors(editedFile, errors);
      return;
    }

    editedFile = this.formatFileData(editedFile);

    this.setState({
      saving: true,
    });

    const result = await updateThemeConfig(editedFile.id, editedFile.file_data);

    if (result?.error) {
      notifyError(result.error);
    } else {
      notifySuccess(`Saved ${editedFile.file_path.split('/').pop()}`);
      this.setState(
        {
          editedFiles: editedFiles.filter((file) => file.id !== editedFile.id),
          activeFile: { ...editedFile, date_updated: Date.now() },
        },
        () => {
          if (this.state.editedFiles.length === 0) {
            if (onEdited) {
              onEdited(false);
            }
            this.setState({ edited: false });
          }
        },
      );
    }

    this.setState({ saving: false });
  };

  renderFilePath(file) {
    const fileName = file.file_path.replace(/^theme\//, '');

    return (
      <span className="storefront-editor--code-file-tabs--label">
        {fileName}
      </span>
    );
  }

  renderFileHeader() {
    const { activeFile, saving } = this.state;

    const editedFile = this.getEditedFile(activeFile);

    const pathParts = activeFile.file_path.replace(/^theme\//, '').split('/');
    const fileName = pathParts.pop();

    return (
      <>
        <div className="storefront-editor--code-editor--header">
          <div className="storefront-editor--code-editor--fileinfo">
            <span>
              {pathParts.map((part) => (
                <>{part} / </>
              ))}
              <strong>{fileName}</strong>
            </span>
          </div>
          <div className="storefront-editor--code-editor--actions">
            <Button
              size="sm"
              type="default"
              disabled={!editedFile || saving}
              onClick={this.onSaveActiveFile}
            >
              {saving ? <Loading size={16} /> : 'Save changes'}
            </Button>
          </div>
        </div>
        {editedFile?.file_data_errors && (
          <div className="storefront-editor--code-editor--header-errors">
            <ul>
              {editedFile.file_data_errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}
      </>
    );
  }

  renderFileCloseButton(file) {
    const editedFile = this.getEditedFile(file);

    return (
      <a
        href=""
        className={classNames('storefront-editor--code-file-tabs--close', {
          edited: editedFile,
        })}
        onClick={this.onClickCloseFile}
        data-id={file.id}
      >
        {editedFile && <span className="status-dot" />} <Icon fa="times" />
      </a>
    );
  }

  renderOpenFileTabs() {
    const { openFiles, activeFile } = this.state;

    return (
      <ul>
        {openFiles.map((file) => (
          <li
            key={file.id}
            className={classNames({ active: file.id === activeFile?.id })}
            onClick={this.onClickFileTab}
            data-id={file.id}
          >
            {this.renderFilePath(file)}
            {this.renderFileCloseButton(file)}
          </li>
        ))}
      </ul>
    );
  }

  renderActiveCodeEditor() {
    const { activeFile } = this.state;

    if (!activeFile) {
      return (
        <div className="storefront-editor--code-editor--empty">
          <Icon fa="rectangle-code" faType="light" />
          <p>Choose a file to edit</p>
        </div>
      );
    }

    const editedFile = this.getEditedFile(activeFile);
    const editingFile = editedFile || activeFile;

    console.log({ editingFile });

    return (
      <div className="storefront-editor--code-editor--content">
        <StorefrontCodeMirror
          key={`${editingFile.id}_${
            editingFile.date_updated || editingFile.date_created
          }`}
          onChange={this.onChangeActiveFile}
          file={editingFile}
        />
      </div>
    );
  }

  render() {
    const { activeFile, openFiles } = this.state;

    return (
      <div className="storefront-editor--code">
        <div className="storefront-editor--sidebar">
          <StorefrontCodeFileList
            {...this.props}
            activeFile={activeFile}
            openFiles={openFiles}
            onClickFile={this.onClickOpenFile}
          />
        </div>
        <div className="storefront-editor--editor">
          <div className="storefront-editor--code-tabs">
            {this.renderOpenFileTabs()}
          </div>
          <div
            className={classNames('storefront-editor--code-editor', {
              empty: !activeFile,
            })}
          >
            {activeFile && this.renderFileHeader()}
            {this.renderActiveCodeEditor()}
          </div>
        </div>
      </div>
    );
  }
}